/**
 * The privacy policy — one page, plain words, no legalese it can't back up.
 *
 * This page exists for two audiences. The person deciding whether to type
 * their email into the signup form, and App Store Connect, which requires a
 * publicly reachable privacy policy URL before the iOS app can be submitted
 * for review. The app's story is short (the free tier stays on the device;
 * premium note polish sends transcript text, never audio, off device for
 * processing only); the site's story is shorter (one email address, one
 * purpose). The page says both and stops.
 *
 * The removal contact comes from the same build-time variable the
 * point-of-collection notice uses (`VITE_REMOVAL_CONTACT`), so the two can
 * never promise different routes. When it is unset the contact line degrades
 * the same way `buildPrivacyNotice` does — the sentence survives without it.
 *
 * Headings: this page owns exactly one `<h1>` (FR-019(e) applies per page);
 * sections are `<h2>`s via `PageSection`, shared with the support page.
 *
 * Unlike sign-in and not-found (which document their no-footer stance), this
 * page carries the site footer deliberately: App Store reviewers and
 * policy-link followers land here cold, and the footer is both the
 * `contentinfo` landmark and the way back into the rest of the site.
 */

import { createFileRoute } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { Footer } from "@/components/marketing/footer";
import { PageColumn } from "@/components/page-column";
import { PageSection } from "@/components/page-section";
import { useAppTheme } from "@/theme/paper-theme";
import { headingProps } from "@/lib/heading-props";
import { removalSentence } from "@/lib/privacy-notice";
import { spacing } from "@/theme/tokens";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicyPage,
  head: () => ({ meta: [{ title: "Privacy Policy — Sparkie" }] }),
});

/** Bumped by hand when the substance (not the phrasing) of a promise moves. */
const EFFECTIVE_DATE = "September 1, 2026";

const CONTACT = (import.meta.env.VITE_REMOVAL_CONTACT ?? "").trim();

// Spread from constants per lib/heading-props.ts: `aria-level` is not in RN's
// prop types, and an inline attribute would be excess-property-checked.
const PAGE_HEADING_PROPS = headingProps(1);

function PrivacyPolicyPage() {
  const theme = useAppTheme();
  const muted = { color: theme.colors.onSurfaceVariant };

  return (
    <PageColumn>
      <View role="main" style={styles.page}>
        <View style={styles.section}>
          <Text variant="headlineLarge" {...PAGE_HEADING_PROPS}>
            Privacy Policy
          </Text>
          <Text variant="bodySmall" style={muted}>
            Effective {EFFECTIVE_DATE}
          </Text>
          <Text variant="bodyLarge">
            Sparkie is built to hold as little of your data as possible. The
            free app keeps everything on your iPhone. The optional premium
            subscription needs an account (an email address and a password) and
            sends exactly one thing off the device: transcript text, so it can
            be cleaned up. This website collects one thing: an email address you
            choose to give us.
          </Text>
        </View>

        <PageSection title="The Sparkie iOS app">
          <Text variant="bodyMedium">
            Your recordings, transcripts, titles, tags, and links are stored as
            ordinary files on your device, in a folder you own
            (Documents/Sparkie). On the free tier everything runs on the device
            itself: recording, transcription, and note linking, with nothing
            sent to us or to anyone else. Your audio never leaves your iPhone on
            any tier. No account is required — the free tier needs no sign-in
            and sends nothing — and the app has no analytics, no advertising,
            and no tracking.
          </Text>
          <Text variant="bodyMedium">
            The first time certain features run, the app downloads the language
            and embedding model files they need (from Apple and from the model
            publisher's servers). Those downloads fetch files; they do not carry
            your notes, your audio, or anything about you beyond the network
            request itself.
          </Text>
          <Text variant="bodyMedium">
            The microphone permission is used only while you record. Recognition
            of your speech happens on the device. Deleting a note in the app (or
            in the Files app) deletes its files; deleting the app removes the
            vault from the device, except copies you have moved elsewhere or
            included in your own device backups.
          </Text>
        </PageSection>

        <PageSection title="Premium note polish">
          <Text variant="bodyMedium">
            Premium note polish is the one feature that uses the network. When a
            note is polished, its transcript text (never the audio) is sent to
            Sparkie's service, which passes it to a model provider to produce
            cleaned-up prose, a title, and a tag. The text is processed, not
            stored: it exists on our service only long enough to do the work and
            hand the result back, it is never written to our logs, and it is not
            used for anything else. The result is saved into the note file on
            your device, where it lives like everything else you own.
          </Text>
          <Text variant="bodyMedium">
            Polish requests are authenticated as your Sparkie account, used only
            to check that the subscription is active and to apply fair-use
            limits. The request carries your transcript and your sign-in —
            nothing else about you.
          </Text>
        </PageSection>

        <PageSection title="Your account">
          <Text variant="bodyMedium">
            Premium features need a Sparkie account: an email address and a
            password. We store the email and a securely hashed form of the
            password (bcrypt) — nothing else — and use them only to sign you in
            and to send a password reset code when you ask for one. Your notes
            never move to the account: they stay on your device either way. You
            can delete the account at any time from the app's Settings; deletion
            removes your email and sign-in from our servers immediately, leaves
            your notes untouched on the device, and does not cancel a
            subscription — that stays managed through Apple.
          </Text>
        </PageSection>

        <PageSection title="Purchases">
          <Text variant="bodyMedium">
            Subscriptions are purchased through Apple on your Apple account. To
            know whether premium is active, the app shares your purchase history
            with the subscription service we use (RevenueCat), under your
            Sparkie account id — so a subscription can follow your account onto
            a new device. RevenueCat sees that id and your purchase history,
            never your notes.
          </Text>
        </PageSection>

        <PageSection title="This website">
          <Text variant="bodyMedium">
            If you join the beta list we store your email address and use it
            only to tell you when the beta opens. It's never shared or sold.
            {` ${removalSentence(CONTACT)}`}
          </Text>
          <Text variant="bodyMedium">
            The site loads no analytics, advertising, or tracking scripts. It
            sets a strictly functional session cookie and nothing else. Our
            hosting provider keeps standard, short-lived request logs (such as
            IP addresses), which we use only to operate the site.
          </Text>
        </PageSection>

        <PageSection title="Children">
          <Text variant="bodyMedium">
            Sparkie is not directed to children under 13, and we do not
            knowingly collect personal information from them.
          </Text>
        </PageSection>

        <PageSection title="Changes">
          <Text variant="bodyMedium">
            If a promise on this page changes, the change appears here with a
            new effective date.
          </Text>
        </PageSection>

        <PageSection title="Contact">
          {/* Different copy from the removal sentence, not a fork of it. */}
          <Text variant="bodyMedium">
            {CONTACT.length > 0
              ? `Questions about any of this: ${CONTACT}.`
              : "Questions about any of this: use the contact route in the app listing."}
          </Text>
        </PageSection>
      </View>
      <Footer />
    </PageColumn>
  );
}

const styles = StyleSheet.create({
  page: {
    gap: spacing.xl,
    // Readable measure inside the wide brand column; the horizontal inset is
    // the same gutter every marketing section carries.
    maxWidth: 760,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xl,
  },
  section: {
    gap: spacing.sm,
  },
});
